import { useEffect, useState } from 'react';
import { X, User, Layers, CalendarClock } from 'lucide-react';
import StaffProfileTab from './StaffProfileTab';
import StaffServicesTab from './StaffServicesTab';
import StaffAvailabilityTab from './StaffAvailabilityTab';

type Tab = 'profile' | 'services' | 'availability';

/**
 * One provider — trainer, therapist, consultant — from the Team page:
 * who they are to members (Profile), what they deliver and at what price
 * (Services), and when (Availability). Salary, attendance and leave stay on
 * the HR tabs.
 */
export default function StaffDetailDrawer({ member, onClose, initialTab = 'profile' }: {
  member: { id: string; name: string; displayName?: string; role: string };
  onClose: () => void;
  initialTab?: Tab;
}) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const name = member.displayName || member.name;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tabs: Array<{ id: Tab; label: string; icon: JSX.Element }> = [
    { id: 'profile', label: 'Profile', icon: <User className="w-4 h-4" /> },
    { id: 'services', label: 'Services', icon: <Layers className="w-4 h-4" /> },
    { id: 'availability', label: 'Availability', icon: <CalendarClock className="w-4 h-4" /> },
  ];

  return (
    <div className="fixed inset-0 z-[70] flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-full max-w-5xl h-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-zinc-100">
          <div className="min-w-0">
            <div className="text-lg font-black text-zinc-900 truncate">{name}</div>
            <div className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.18em]">{member.role}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-zinc-100" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-1 px-4 border-b border-zinc-100 overflow-x-auto">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 text-[11px] font-black uppercase tracking-[0.14em] whitespace-nowrap transition-colors ${tab === t.id ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-700'}`}
            >
              {t.icon}{t.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto">
          {tab === 'profile' && <StaffProfileTab mappingId={member.id} />}
          {tab === 'services' && <StaffServicesTab mappingId={member.id} name={name} />}
          {tab === 'availability' && <StaffAvailabilityTab mappingId={member.id} />}
        </div>
      </div>
    </div>
  );
}
